// ============================================================================
// USE TABLETOP WAR ROOM AUDIO - Plain <audio> cue playback for the war room
// scene (plan 06). No sound library for four cues, per the overhaul's shared
// constraints — same reasoning BreakingNewsOverlay already applies to its own
// (pre-existing, separately managed) breaking-news sting.
//
// Every cue independently tolerates a missing asset the same way
// BreakingNewsOverlay's STING_URL does: `.play().catch(() => {})`, never
// blocking anything else. Mute defaults to on and persists in localStorage —
// browsers block autoplay until a user gesture unlocks it, and a facilitator
// opening a second screen mid-session doesn't want unexpected audio.
// ============================================================================

import { useCallback, useEffect, useRef, useState } from 'react';
import type { TabletopSecondScreenState } from '../types/second-screen.types';

const MUTE_KEY = 'tabletop-war-room-muted';
const AMBIENT_URL = '/assets/tabletop/ambient-room.mp3';
const CRITICAL_STING_URL = '/assets/tabletop/alert-critical.mp3';
const DECISION_TONE_URL = '/assets/tabletop/decision-logged.mp3';
const CRITICAL_STING_THROTTLE_MS = 20_000;

// Low always — this plays continuously under everything else in the room —
// lifting only slightly as pressure climbs (plan 06).
const AMBIENT_VOLUME_BY_LEVEL: Record<0 | 1 | 2 | 3 | 4, number> = {
  0: 0.08,
  1: 0.12,
  2: 0.16,
  3: 0.22,
  4: 0.28,
};

export interface TabletopWarRoomAudioControls {
  muted: boolean;
  toggleMuted: () => void;
}

export function useTabletopWarRoomAudio(
  tabletop: TabletopSecondScreenState | null | undefined,
  // Caller-computed effective pressure level, not tabletop.pressureLevel
  // directly — TabletopWarRoomWidget falls back to a telemetry-derived level
  // before the adjudicator has produced its first reading, and the ambient
  // bed's volume needs to track whatever the visual frame is actually
  // showing, not the raw (possibly stale-zero) backend field.
  effectivePressureLevel: 0 | 1 | 2 | 3 | 4
): TabletopWarRoomAudioControls {
  const [muted, setMuted] = useState<boolean>(() => {
    try {
      // Absence of a stored preference means "never explicitly unmuted" —
      // defaults to muted, not to whatever the stored string parses to.
      return localStorage.getItem(MUTE_KEY) !== 'false';
    } catch {
      return true;
    }
  });

  const ambientRef = useRef<HTMLAudioElement | null>(null);
  const criticalStingRef = useRef<HTMLAudioElement | null>(null);
  const decisionToneRef = useRef<HTMLAudioElement | null>(null);
  const lastCriticalStingAtRef = useRef(0);
  const seenCriticalSeqsRef = useRef<Set<number>>(new Set());
  const seenDecisionIdsRef = useRef<Set<string>>(new Set());
  const isFirstTelemetryRun = useRef(true);
  const isFirstDecisionRun = useRef(true);

  // Ambient is created eagerly since it must be ready to loop the instant the
  // mute toggle unlocks it. The two one-shot cues are constructed lazily on
  // first actual use instead (getOrCreate below) — eagerly constructing them
  // at mount would fire a network request per cue on every widget mount even
  // in a deployment that never ships those optional assets.
  useEffect(() => {
    const ambient = new Audio(AMBIENT_URL);
    ambient.loop = true;
    ambient.volume = AMBIENT_VOLUME_BY_LEVEL[0];
    ambientRef.current = ambient;

    return () => {
      ambient.pause();
      ambientRef.current = null;
      criticalStingRef.current = null;
      decisionToneRef.current = null;
    };
  }, []);

  const getOrCreateAudio = useCallback((ref: React.MutableRefObject<HTMLAudioElement | null>, url: string): HTMLAudioElement => {
    if (!ref.current) ref.current = new Audio(url);
    return ref.current;
  }, []);

  const toggleMuted = useCallback(() => {
    setMuted((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(MUTE_KEY, String(next));
      } catch {
        // localStorage unavailable (private browsing, etc.) — preference
        // just won't persist across reloads, nothing else depends on it.
      }
      return next;
    });
  }, []);

  // Start/stop the ambient bed as the mute toggle flips. The toggle click
  // itself is the user gesture that unlocks autoplay, so calling play() here
  // (inside the effect the click's state update triggers) succeeds under
  // browsers' sticky-activation rules; if it doesn't, the catch is silent.
  useEffect(() => {
    const ambient = ambientRef.current;
    if (!ambient) return;
    if (muted) {
      ambient.pause();
      return;
    }
    ambient.play().catch(() => {});
  }, [muted]);

  useEffect(() => {
    const ambient = ambientRef.current;
    if (!ambient) return;
    ambient.volume = AMBIENT_VOLUME_BY_LEVEL[effectivePressureLevel];
  }, [effectivePressureLevel]);

  const telemetryKey = tabletop?.telemetry.map((t) => `${t.seq}:${t.severity}`).join(',') ?? '';

  // Critical alert sting, throttled to at most one per 20s. Diffed
  // independently of AlertStream's own arrival-animation diffing — audio and
  // the visual arrival effect are separate concerns reacting to the same
  // event, not state that should be shared across components.
  useEffect(() => {
    const telemetry = tabletop?.telemetry ?? [];
    if (isFirstTelemetryRun.current) {
      isFirstTelemetryRun.current = false;
      telemetry.forEach((t) => seenCriticalSeqsRef.current.add(t.seq));
      return;
    }
    const newCritical = telemetry.filter((t) => t.severity === 'critical' && !seenCriticalSeqsRef.current.has(t.seq));
    telemetry.forEach((t) => seenCriticalSeqsRef.current.add(t.seq));
    if (newCritical.length === 0 || muted) return;
    const now = Date.now();
    if (now - lastCriticalStingAtRef.current < CRITICAL_STING_THROTTLE_MS) return;
    lastCriticalStingAtRef.current = now;
    getOrCreateAudio(criticalStingRef, CRITICAL_STING_URL).play().catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [telemetryKey, muted]);

  const decisionKey = tabletop?.recentDecisions.map((d) => d.eventId).join(',') ?? '';

  // Decision-landing tone — no throttle. Decisions are rare enough relative
  // to telemetry noise that plan 06 doesn't ask for one.
  useEffect(() => {
    const decisions = tabletop?.recentDecisions ?? [];
    if (isFirstDecisionRun.current) {
      isFirstDecisionRun.current = false;
      decisions.forEach((d) => seenDecisionIdsRef.current.add(d.eventId));
      return;
    }
    const newlySeen = decisions.filter((d) => !seenDecisionIdsRef.current.has(d.eventId));
    decisions.forEach((d) => seenDecisionIdsRef.current.add(d.eventId));
    if (newlySeen.length === 0 || muted) return;
    getOrCreateAudio(decisionToneRef, DECISION_TONE_URL).play().catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [decisionKey, muted]);

  return { muted, toggleMuted };
}
